"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { StepIndicatorV2, type StepDef } from "./StepIndicatorV2";
import ButtonV2 from "./ButtonV2";

interface WizardShellProps {
  steps: StepDef[];
  activeIndex: number;
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function WizardShell({
  steps,
  activeIndex,
  onBack,
  onNext,
  nextLabel,
  backLabel,
  nextDisabled = false,
  loading = false,
  children,
  className,
}: WizardShellProps) {
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === steps.length - 1;

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Skip if event was already handled by an overlay (e.g. ConfirmDialogV2)
      if (e.defaultPrevented) return;
      // Skip if ESC originates from inside a dialog/modal overlay
      const target = e.target as HTMLElement | null;
      if (target?.closest('[role="dialog"]')) return;

      if (e.key === "Escape" && !isFirst) {
        e.preventDefault();
        onBack();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isFirst, onBack]);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <StepIndicatorV2 steps={steps} activeIndex={activeIndex} />

      <div className="v2-motion-page-enter min-h-[200px]">{children}</div>

      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-[var(--v2-border)] bg-[var(--v2-surface)] px-1 py-3">
        <ButtonV2
          type="button"
          variant="secondary"
          onClick={onBack}
          disabled={isFirst || loading}
        >
          {backLabel || "Geri"}
        </ButtonV2>
        <ButtonV2
          type="button"
          onClick={onNext}
          disabled={nextDisabled || loading}
        >
          {loading
            ? "Isleniyor..."
            : nextLabel || (isLast ? "Yayinla" : "Ileri")}
        </ButtonV2>
      </div>
    </div>
  );
}
